import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import useHighlightParam from "@/hooks/useHighlightParam";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Truck, Trash2, Globe, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { ContactDeleteDialog } from "@/components/ContactDeleteDialog";
import { ZefixExportButton } from "@/components/ZefixExportButton";
import { ZefixEnrichButton, ZefixStatusBadge, toastEnriched } from "@/components/ZefixEnrichButton";
import { AutoSupplierConfirm } from "@/components/AutoSupplierConfirm";

export function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    address: "",
    postal_code: "",
    city: "",
    vat_id: "",
    iban: "",
    reverse_charge: false,
  });
  const [loading, setLoading] = useState(false);
  const toggleReverseCharge = async (s) => {
    try {
      const r = await apiClient.put(`/companies/${companyId}/contacts/${s._id}/reverse-charge`, { enabled: !s.reverse_charge });
      setSuppliers((prev) => prev.map((x) => (x._id === s._id ? { ...x, reverse_charge: r.data.reverse_charge } : x)));
      toast.success(r.data.reverse_charge ? `${s.name}: Bezugsteuer wird bei jeder Rechnung automatisch gebucht` : `${s.name}: keine Bezugsteuer mehr`);
    } catch (e) { toast.error(e.response?.data?.detail || "Änderung fehlgeschlagen"); }
  };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterAuto, setFilterAuto] = useState(false);
  const autoCount = suppliers.filter((s) => s.source === "auto_inbox").length;
  const shown = filterAuto ? suppliers.filter((s) => s.source === "auto_inbox") : suppliers;

  const companyId = localStorage.getItem("demoCompanyId");

  const loadSuppliers = async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get(`/companies/${companyId}/contacts`, {
        params: { type: "supplier" },
      });
      setSuppliers(res.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden der Lieferanten.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuppliers();
  }, []);

  useHighlightParam(suppliers, (s) => s._id || s.id);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!companyId) {
      setError("Kein Mandant gefunden. Bitte Seite neu laden.");
      return;
    }
    if (!form.name.trim()) {
      setError("Name ist ein Pflichtfeld.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        type: "supplier",
        name: form.name.trim(),
        email: form.email || null,
        address: form.address || null,
        postal_code: form.postal_code || null,
        city: form.city || null,
        vat_id: form.vat_id || null,
        iban: form.iban ? form.iban.replace(/\s+/g, "").toUpperCase() : null,
        reverse_charge: form.reverse_charge || null,
      };
      const res = await apiClient.post(`/companies/${companyId}/contacts`, payload);
      toastEnriched(payload, res.data);
      setSuppliers((prev) => [...prev, res.data]);
      setForm({ name: "", email: "", address: "", postal_code: "", city: "", vat_id: "", iban: "", reverse_charge: false });
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Anlegen des Lieferanten.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Lieferanten</h1>
          <p className="mt-1 text-sm text-slate-500">
            Verwalten Sie Ihre Lieferantenstammdaten für Eingangsrechnungen.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ZefixEnrichButton companyId={companyId} onDone={() => loadSuppliers()} testId="suppliers-hr-enrich-btn" />
          <ZefixExportButton
            url={`/companies/${companyId}/zefix/export-contacts`}
            body={{ contact_type: "supplier" }}
            label="Handelsregister-Abgleich (CSV)"
            testId="suppliers-hr-export-btn"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <Card className="p-4 max-w-xl border border-slate-200 bg-white shadow-sm" data-testid="suppliers-form-card">
        <form className="space-y-3" onSubmit={handleCreate} data-testid="suppliers-form">
          <div className="space-y-1">
            <Label htmlFor="supplier-name">Name</Label>
            <Input
              id="supplier-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="suppliers-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="supplier-address">Adresse</Label>
            <Input
              id="supplier-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="suppliers-address-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="supplier-postal">PLZ</Label>
              <Input
                id="supplier-postal"
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                data-testid="suppliers-postal-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="supplier-city">Ort</Label>
              <Input
                id="supplier-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="suppliers-city-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="supplier-vat">MWST-/UID-Nr.</Label>
              <Input
                id="supplier-vat"
                value={form.vat_id}
                onChange={(e) => setForm({ ...form, vat_id: e.target.value })}
                data-testid="suppliers-vat-input"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="supplier-email">E-Mail</Label>
            <Input
              id="supplier-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="suppliers-email-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="supplier-iban">IBAN (für Zahlungslauf)</Label>
            <Input
              id="supplier-iban"
              value={form.iban}
              onChange={(e) => setForm({ ...form, iban: e.target.value })}
              placeholder="CH00 0000 0000 0000 0000 0"
              data-testid="suppliers-iban-input"
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700 sm:col-span-2" data-testid="suppliers-reverse-charge-label">
            <input type="checkbox" className="mt-0.5" checked={form.reverse_charge} onChange={(e) => setForm({ ...form, reverse_charge: e.target.checked })} data-testid="suppliers-reverse-charge-checkbox" />
            <span><span className="font-medium">Dienstleister im Ausland – Bezugsteuer</span><br /><span className="text-xs text-slate-500">Rechnungen ohne Schweizer MWST (z. B. OpenAI, Anthropic): Bezugsteuer 8.1 % wird automatisch erfasst (Ziffer 382, bei effektiver Methode gleichzeitig als Vorsteuer).</span></span>
          </label>
          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-4"
              disabled={saving}
              data-testid="suppliers-save-button"
            >
              {saving ? "Speichern ..." : "Lieferant anlegen"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="suppliers-list-card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-medium text-slate-700">{filterAuto ? "Automatisch angelegte Lieferanten" : "Alle Lieferanten"}</h2>
            <button type="button" onClick={() => setFilterAuto((v) => !v)} data-testid="suppliers-filter-auto"
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${filterAuto ? "border-amber-400 bg-amber-100 text-amber-800" : autoCount ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100" : "border-slate-200 text-slate-400"}`}
              disabled={!autoCount && !filterAuto}>
              <Sparkles className="h-3 w-3" /> Automatisch angelegt ({autoCount}){filterAuto ? " · Filter aus" : ""}
            </button>
          </div>
          {loading && (
            <span className="text-xs text-slate-400" data-testid="suppliers-loading">
              Lade ...
            </span>
          )}
        </div>
        <Table data-testid="suppliers-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Adresse</TableHead>
              <TableHead>PLZ / Ort</TableHead>
              <TableHead>E-Mail</TableHead>
              <TableHead>IBAN</TableHead>
              <TableHead>MWST-/UID-Nr.</TableHead>
              <TableHead title="Bezugsteuer (Dienstleister im Ausland)">Bezugsteuer</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <EmptyState
                    testid="suppliers-empty-state"
                    icon={<Truck className="h-5 w-5" />}
                    title={filterAuto ? "Keine unbestätigten automatischen Lieferanten" : "Noch keine Lieferanten"}
                    description={filterAuto ? "Alle automatisch aus Belegen angelegten Lieferanten sind geprüft." : "Legen Sie einen Lieferanten an, um Eingangsrechnungen zuzuordnen und Zahlungen zu erfassen."}
                  />
                </TableCell>
              </TableRow>
            )}
            {shown.map((s) => (
              <TableRow key={s._id} data-testid={`suppliers-row-${s._id}`} data-highlight-id={s._id}>
                <TableCell>
                  {s.name}
                  {s.source === "auto_inbox" && (
                    <AutoSupplierConfirm companyId={companyId} supplier={s}
                      onConfirmed={(c) => setSuppliers((prev) => prev.map((x) => (x._id === s._id ? { ...x, ...c, _id: s._id } : x)))} />
                  )}
                </TableCell>
                <TableCell>{s.address}</TableCell>
                <TableCell>
                  {s.postal_code} {s.city}
                </TableCell>
                <TableCell>{s.email}</TableCell>
                <TableCell className="font-mono text-xs">{s.iban || ""}</TableCell>
                <TableCell>{s.vat_id}<ZefixStatusBadge contact={s} /></TableCell>
                <TableCell>
                  <button type="button" onClick={() => toggleReverseCharge(s)} data-testid={`suppliers-reverse-charge-toggle-${s._id}`}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${s.reverse_charge ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-400 hover:text-slate-600"}`}>
                    <Globe className="h-3 w-3" /> {s.reverse_charge ? "Ausland · 8.1 %" : "nein"}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    onClick={() => setDeleteTarget(s)}
                    data-testid={`suppliers-delete-button-${s._id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Löschen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ContactDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        companyId={companyId}
        contact={deleteTarget}
        kind="Lieferant"
        onDeleted={(id) => setSuppliers((prev) => prev.filter((x) => x._id !== id))}
      />
    </div>
  );
}
